import type {
    GdprAccessScope,
    GdprObjectionProcessing,
    GdprPortabilityFormat,
    GdprRectificationField,
    GdprRequestType,
    GdprRestrictionReason,
} from "@/app/constants/gdpr-rights"

const BASE_API = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/common/gdpr`

export type GdprRequestDetails =
    | {scope: GdprAccessScope; scopeDetails?: string}
    | {field: GdprRectificationField; currentValue?: string; requestedValue: string}
    | {reason: GdprRestrictionReason; details: string}
    | {format: GdprPortabilityFormat}
    | {processing: GdprObjectionProcessing; reason?: string}
    | Record<string, never>

// An erasure sends the email alone (the confirmation link proves the mailbox); the other rights add the names and the two certifications
export interface GdprRequestPayload {
    type: GdprRequestType
    firstName?: string
    lastName?: string
    email: string
    identityCertified?: true
    privacyAccepted?: true
    turnstileToken: string | null
    details: GdprRequestDetails
}

export type GdprSubmitError = "identity_not_verified" | "turnstile" | "rate_limited" | "network" | "unknown"

export type GdprSubmitResult =
    | {ok: true}
    | {ok: false; error: GdprSubmitError; message?: string}
    | {ok: false; error: "validation"; errors: Record<string, string>}

export type GdprConfirmOutcome = "applied" | "pending" | "failed"

export type GdprConfirmResult =
    | {status: "confirmed"; type: GdprRequestType; outcome: GdprConfirmOutcome; message: string}
    | {status: "not_found" | "expired" | "already_confirmed" | "error"}

export type GdprExportInfoResult =
    | {status: "available"; type: GdprRequestType; fileName: string; expiresAt: string}
    | {status: "not_found" | "expired" | "error"}

async function readJson(response: Response): Promise<any | null> {
    return response.json().catch(() => null)
}

function buildRequestBody(payload: GdprRequestPayload, attachment: File | null): {body: BodyInit; headers: HeadersInit} {
    if (!attachment) {
        return {body: JSON.stringify(payload), headers: {"Content-Type": "application/json"}}
    }

    const formData = new FormData()
    formData.append("type", payload.type)
    if (payload.firstName !== undefined) formData.append("firstName", payload.firstName)
    if (payload.lastName !== undefined) formData.append("lastName", payload.lastName)
    formData.append("email", payload.email)
    if (payload.identityCertified) formData.append("identityCertified", "1")
    if (payload.privacyAccepted) formData.append("privacyAccepted", "1")
    if (payload.turnstileToken) formData.append("turnstileToken", payload.turnstileToken)
    formData.append("details", JSON.stringify(payload.details))
    formData.append("attachment", attachment, attachment.name)

    // No Content-Type: the browser sets the multipart boundary itself
    return {body: formData, headers: {}}
}

export async function postGdprRequest(payload: GdprRequestPayload, attachment: File | null = null): Promise<GdprSubmitResult> {
    try {
        const {body, headers} = buildRequestBody(payload, attachment)
        const response = await fetch(`${BASE_API}/requests`, {method: "POST", headers, body, cache: "no-store"})

        if (response.status === 201) return {ok: true}

        const result = await readJson(response)

        if (response.status === 422 && result?.error === "identity_not_verified") {
            return {ok: false, error: "identity_not_verified", message: result?.message}
        }
        if (response.status === 422 && result?.error === "validation") {
            return {ok: false, error: "validation", errors: result?.errors ?? {}}
        }
        if (response.status === 403 && result?.error === "turnstile") return {ok: false, error: "turnstile"}
        if (response.status === 429) return {ok: false, error: "rate_limited"}

        return {ok: false, error: "unknown", message: result?.message}
    } catch (error) {
        console.error("Error posting GDPR request:", error)
        return {ok: false, error: "network"}
    }
}

export async function confirmGdprRequest(confirmationToken: string): Promise<GdprConfirmResult> {
    try {
        const response = await fetch(`${BASE_API}/requests/${confirmationToken}/confirm`, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            cache: "no-store",
        })

        if (response.ok) {
            const result = await readJson(response)
            if (!result?.message) return {status: "error"}
            return {status: "confirmed", type: result.type, outcome: result.outcome ?? "applied", message: result.message}
        }
        if (response.status === 404) return {status: "not_found"}
        if (response.status === 410) return {status: "expired"}
        if (response.status === 409) return {status: "already_confirmed"}

        return {status: "error"}
    } catch (error) {
        console.error("Error confirming GDPR request:", error)
        return {status: "error"}
    }
}

export function gdprExportDownloadUrl(downloadToken: string): string {
    return `${BASE_API}/exports/${downloadToken}`
}

export async function getGdprExportInfo(downloadToken: string): Promise<GdprExportInfoResult> {
    try {
        const response = await fetch(`${gdprExportDownloadUrl(downloadToken)}/info`, {
            method: "GET",
            headers: {"Content-Type": "application/json"},
            cache: "no-store",
        })

        if (response.ok) {
            const result = await readJson(response)
            // a malformed date would make the page's date formatter throw
            if (typeof result?.expiresAt !== "string" || Number.isNaN(Date.parse(result.expiresAt))) return {status: "error"}
            return {status: "available", type: result.type, fileName: result.fileName ?? "export.zip", expiresAt: result.expiresAt}
        }
        if (response.status === 404) return {status: "not_found"}
        if (response.status === 410) return {status: "expired"}

        return {status: "error"}
    } catch (error) {
        console.error("Error fetching GDPR export info:", error)
        return {status: "error"}
    }
}
